import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, BarChart3, DollarSign, Users, Target,
  ArrowUp, ArrowDown, Minus, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sector colors and icons
const SECTOR_CONFIG = {
  humedades: { color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-500/20', textColor: 'text-blue-400', icon: '💧' },
  jardineria: { color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-500/20', textColor: 'text-green-400', icon: '🌿' },
  reformas: { color: 'from-orange-500 to-amber-500', bgColor: 'bg-orange-500/20', textColor: 'text-orange-400', icon: '🏗️' },
  climatizacion: { color: 'from-indigo-500 to-purple-500', bgColor: 'bg-indigo-500/20', textColor: 'text-indigo-400', icon: '❄️' },
  piscinas: { color: 'from-cyan-500 to-teal-500', bgColor: 'bg-cyan-500/20', textColor: 'text-cyan-400', icon: '🏊' }
};

export function HighTicketDashboard({ adminId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/client-sites/analytics/high-ticket`, {
        headers: { 'X-Admin-Id': adminId }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching high-ticket analytics:', error);
      toast.error('Error al cargar métricas High Ticket');
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No hay datos disponibles</p>
      </div>
    );
  }

  const { sectors, totals } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-amber-400" />
            Dashboard High Ticket
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Métricas de conversión para sectores de alto valor
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
          data-testid="refresh-high-ticket"
        >
          <RefreshCw className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Totals Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-xl p-4 border border-blue-700/30"
        >
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-blue-300">Total Leads</span>
          </div>
          <p className="text-2xl font-bold text-white">{totals.total_leads}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 rounded-xl p-4 border border-emerald-700/30"
        >
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-300">Convertidos</span>
          </div>
          <p className="text-2xl font-bold text-white">{totals.total_converted}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 rounded-xl p-4 border border-purple-700/30"
        >
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-purple-300">% Conversión</span>
          </div>
          <p className="text-2xl font-bold text-white">{totals.avg_conversion_rate}%</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-amber-900/50 to-amber-800/30 rounded-xl p-4 border border-amber-700/30"
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-amber-300">Sitios Activos</span>
          </div>
          <p className="text-2xl font-bold text-white">{totals.total_active_sites}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-xl p-4 border border-green-700/30"
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-green-400" />
            <span className="text-xs text-green-300">Ingresos/Mes</span>
          </div>
          <p className="text-2xl font-bold text-white">{totals.total_monthly_revenue.toFixed(0)}€</p>
        </motion.div>
      </div>

      {/* Sectors Table */}
      <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-semibold text-white">Rendimiento por Sector</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Sector
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Leads
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Presupuestados
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Convertidos
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  % Conv.
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Ticket Medio
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Sitios Activos
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Ingresos/Mes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {sectors.map((sector, index) => {
                const config = SECTOR_CONFIG[sector.sector] || { color: 'from-gray-500 to-gray-600', bgColor: 'bg-gray-500/20', textColor: 'text-gray-400', icon: '📊' };
                
                return (
                  <motion.tr
                    key={sector.sector}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-800/30 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center text-xl`}>
                          {config.icon}
                        </div>
                        <div>
                          <p className="font-medium text-white">{sector.label}</p>
                          <p className="text-xs text-gray-500">{sector.sector}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-lg font-semibold text-white">{sector.total_leads}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-purple-400">{sector.quoted}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-emerald-400 font-medium">{sector.converted}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {sector.conversion_rate > 20 ? (
                          <ArrowUp className="w-3 h-3 text-emerald-400" />
                        ) : sector.conversion_rate > 10 ? (
                          <Minus className="w-3 h-3 text-amber-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-red-400" />
                        )}
                        <span className={`font-medium ${
                          sector.conversion_rate > 20 ? 'text-emerald-400' :
                          sector.conversion_rate > 10 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                          {sector.conversion_rate}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-amber-400 font-medium">
                        {sector.avg_quote > 0 ? `${sector.avg_quote.toFixed(0)}€` : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        sector.active_sites > 0 ? 'bg-emerald-900/50 text-emerald-400' : 'bg-gray-800 text-gray-500'
                      }`}>
                        {sector.active_sites}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-green-400 font-semibold">
                        {sector.monthly_revenue > 0 ? `${sector.monthly_revenue.toFixed(0)}€` : '-'}
                      </span>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 rounded-xl p-4 border border-amber-700/30">
        <h4 className="font-medium text-amber-400 mb-2">💡 Consejos High Ticket</h4>
        <ul className="text-sm text-gray-300 space-y-1">
          <li>• Los sectores con mayor ticket medio (Reformas, Climatización) requieren seguimiento más personalizado</li>
          <li>• Ofrece planes de mantenimiento premium para dominios propios (+60-100€/mes)</li>
          <li>• Usa testimonios de clientes convertidos en las demos de cada sector</li>
        </ul>
      </div>
    </div>
  );
}

export default HighTicketDashboard;
